import { For, Show, createSignal, onMount } from "solid-js";
import { REGISTRATION_LABELS, formatDate, type AdminApi, type AdminInfo, type Paged, type RegistrationFilter, type RegistrationRow } from "./api";

export function RegistrationsView(props: { api: AdminApi; admin: AdminInfo; guard: <T>(work: () => Promise<T>) => Promise<T> }) {
  const [data, setData] = createSignal<Paged<RegistrationRow> & { waiting: number }>({ items: [], total: 0, page: 1, limit: 25, waiting: 0 });
  const [filter, setFilter] = createSignal<RegistrationFilter>("open");
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal("");
  const [open, setOpen] = createSignal<string | null>(null);
  const [decision, setDecision] = createSignal<"approve" | "reject" | null>(null);
  const [saving, setSaving] = createSignal(false);
  const [dialogError, setDialogError] = createSignal("");

  const load = async (page = 1) => {
    setLoading(true);
    setError("");
    try {
      setData(await props.guard(() => props.api.registrations(filter(), page)));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };
  onMount(() => void load());

  const selected = () => data().items.find((item) => item.id === open());
  const pages = () => Math.max(1, Math.ceil(data().total / data().limit));

  const submit = async (event: SubmitEvent) => {
    event.preventDefault();
    const current = selected();
    const kind = decision();
    if (!current || !kind) return;
    const reason = String(new FormData(event.currentTarget as HTMLFormElement).get("reason") ?? "").trim();
    setSaving(true);
    setDialogError("");
    try {
      await props.guard(() => props.api.registrationDecision(current.id, kind, reason));
      setDecision(null);
      setOpen(null);
      await load(data().page);
    } catch (cause) {
      setDialogError(cause instanceof Error ? cause.message : "Aktion fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <div class="adm-head">
        <h2>Anfragen</h2>
        <Show when={data().waiting > 0}><span class="adm-pill trial">{data().waiting} warten auf Freigabe</span></Show>
      </div>
      <div class="adm-bar">
        <select class="adm-input" style={{ "max-width": "260px" }} aria-label="Filter" value={filter()} onChange={(event) => { setFilter(event.currentTarget.value as RegistrationFilter); setOpen(null); void load(1); }}>
          <option value="open">Offen (unbestätigt, wartend, freigegeben)</option>
          <option value="waiting">Nur wartend auf Freigabe</option>
          <option value="all">Alle</option>
        </select>
      </div>
      <Show when={error()}><p class="adm-error" role="alert">{error()}</p></Show>

      <Show when={selected()}>
        {(registration) => (
          <div class="adm-card adm-dialog">
            <div class="adm-head" style={{ "margin-bottom": "8px" }}>
              <h3 style={{ margin: 0 }}>{registration().schoolName}</h3>
              <span class="adm-pill">{REGISTRATION_LABELS[registration().status]}</span>
              <button class="adm-btn ghost small" type="button" onClick={() => { setOpen(null); setDecision(null); }}>Schließen</button>
            </div>
            <dl class="adm-facts">
              <dt>Adresse</dt><dd>{registration().street}, {registration().postalCode} {registration().city}</dd>
              <dt>Schulform</dt><dd>{registration().schoolType ?? "–"}</dd>
              <dt>Schulnummer</dt><dd>{registration().schoolNumber ?? "nicht angegeben"}</dd>
              <dt>Klassen (geschätzt)</dt><dd>{registration().estClasses ?? "–"}</dd>
              <dt>Ansprechperson</dt><dd>{registration().contactName}, {registration().contactFunction}</dd>
              <dt>E-Mail</dt><dd>{registration().contactEmail}</dd>
              <dt>Angefragt</dt><dd>{formatDate(registration().createdAt)}, E-Mail bestätigt: {registration().confirmedAt ? formatDate(registration().confirmedAt) : "noch nicht"}</dd>
              <Show when={registration().reviewedAt}><dt>Entscheidung</dt><dd>{formatDate(registration().reviewedAt)}: {registration().reviewReason}</dd></Show>
            </dl>
            <Show when={registration().hints.freemail || (registration().hints.similar ?? 0) > 0}>
              <p class="adm-warn" style={{ margin: "12px 0 0" }}>
                Hinweise zur Prüfung:
                {registration().hints.freemail ? " private Mail-Adresse (z. B. gmx, gmail)." : ""}
                {(registration().hints.similar ?? 0) > 0 ? ` Ähnliche Schule oder Anfrage schon vorhanden (${registration().hints.similar}).` : ""}
              </p>
            </Show>
            <Show when={registration().status === "awaiting_approval"}>
              <Show when={props.admin.canApprove} fallback={<p class="adm-sub" style={{ "margin-top": "12px" }}>Dein Admin-Konto darf Anfragen ansehen, aber nicht freigeben oder ablehnen.</p>}>
                <div class="adm-actions" style={{ "margin-top": "14px" }}>
                  <button class="adm-btn small" type="button" onClick={() => { setDecision("approve"); setDialogError(""); }}>Freigeben</button>
                  <button class="adm-btn ghost small" type="button" onClick={() => { setDecision("reject"); setDialogError(""); }}>Ablehnen</button>
                </div>
              </Show>
            </Show>
            <Show when={decision()}>
              {(kind) => (
                <form onSubmit={submit} style={{ "margin-top": "14px" }}>
                  <p class="adm-sub">
                    {kind() === "approve"
                      ? "Die Schulleitung bekommt eine Mail mit dem Link für Passwort und Vertrag."
                      : "Die Schulleitung erfährt nur, dass die Anfrage nicht freigeschaltet wurde. Der Grund bleibt intern."}
                  </p>
                  <label class="adm-field"><span>Grund (mindestens 5 Zeichen, steht im Protokoll)</span><textarea name="reason" class="adm-input" required minlength={5} maxlength={500} autofocus /></label>
                  <div class="adm-bar">
                    <button class={`adm-btn ${kind() === "reject" ? "danger" : ""}`} type="submit" disabled={saving()}>{kind() === "approve" ? "Jetzt freigeben" : "Jetzt ablehnen"}</button>
                    <button class="adm-btn ghost" type="button" onClick={() => setDecision(null)}>Abbrechen</button>
                  </div>
                  <Show when={dialogError()}><p class="adm-error" role="alert">{dialogError()}</p></Show>
                </form>
              )}
            </Show>
          </div>
        )}
      </Show>

      <div class="adm-card">
        <div class="adm-table-wrap">
          <table class="adm-table">
            <thead><tr><th>Schule</th><th>Ort</th><th>Ansprechperson</th><th>Angefragt</th><th>Status</th><th>Hinweise</th></tr></thead>
            <tbody>
              <For each={data().items}>
                {(row) => (
                  <tr>
                    <td><button class="adm-link" type="button" onClick={() => { setOpen(row.id); setDecision(null); }}>{row.schoolName}</button></td>
                    <td>{row.city}</td>
                    <td>{row.contactName}</td>
                    <td>{formatDate(row.createdAt)}</td>
                    <td><span class={`adm-pill ${row.status === "awaiting_approval" ? "trial" : row.status === "rejected" || row.status === "expired" ? "deleted" : "active"}`}>{REGISTRATION_LABELS[row.status]}</span></td>
                    <td>{[row.hints.freemail ? "private Adresse" : "", (row.hints.similar ?? 0) > 0 ? "ähnlich vorhanden" : ""].filter(Boolean).join(", ") || "–"}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
        <Show when={!loading() && data().items.length === 0}><p class="adm-empty">Keine Anfragen in dieser Ansicht.</p></Show>
        <div class="adm-pager">
          <button class="adm-btn ghost small" type="button" disabled={data().page <= 1 || loading()} onClick={() => void load(data().page - 1)}>Zurück</button>
          <span>Seite {data().page} von {pages()} · {data().total} Anfragen</span>
          <button class="adm-btn ghost small" type="button" disabled={data().page >= pages() || loading()} onClick={() => void load(data().page + 1)}>Weiter</button>
        </div>
      </div>
    </section>
  );
}
